import { CheckCircle } from "@similarweb/icons";
import type { ColumnType } from "@similarweb/pro-logic-components";
import { ChipOverflowCell, IntentCell } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Flex } from "@similarweb/ui-components/foundations";

import { UrlCellWebsiteAnalysis } from "app/components/React/Table/cells";
import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { percentageFilter } from "app/filters/ngFilters";
import { SourceBadge } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/source-badge/SourceBadge";
import type { TopicSourceType } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/source-badge/types";
import { StyledMoreDetailsIcon } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/PromptsComponent.styled";
import { ChangeStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/types";
import type { ISortingData } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/tableReducer";

import type { IAnalyzedURL } from "./types";

export const getColumnConfig = (
    sorting: ISortingData<keyof IAnalyzedURL>,
    totalUrls: number,
    hasCompetitors: boolean,
): Array<ColumnType<IAnalyzedURL>> => [
    {
        key: "url",
        dataIndex: "url",
        title: translate("llm.sources_report.url_analysis.table.column.url.title", {
            count: totalUrls.toLocaleString(),
        }),
        displayName: translate("llm.sources_report.url_analysis.table.column.url.display_name"),
        tooltipSubtitle: translate("llm.sources_report.url_analysis.table.column.url.tooltip"),
        sortOrder: sorting.dataIndex === "url" ? sorting.sortDirection : undefined,
        sorter: true,
        align: "left",
        width: 400,
        fixed: true,
        render: (url: string) => (
            <Flex
                alignItems="center"
                justifyContent="space-between"
                height="20px"
            >
                <UrlCellWebsiteAnalysis
                    value={url}
                    displayedSite={url}
                />
                <StyledMoreDetailsIcon
                    iconName="show-details"
                    size="sm"
                />
            </Flex>
        ),
    },
    {
        key: "sourceAuthority",
        dataIndex: "sourceAuthority",
        title: translate("llm.sources_report.url_analysis.table.column.source_authority.title"),
        tooltipSubtitle: translate("llm.sources_report.url_analysis.table.column.source_authority.tooltip"),
        sortOrder: sorting.dataIndex === "sourceAuthority" ? sorting.sortDirection : undefined,
        sorter: true,
        align: "left",
        width: 125,
        render: (value: number) => percentageFilter()(value, 2),
    },
    {
        key: "changeStatus",
        dataIndex: "changeStatus",
        title: translate("llm.prompts.column.change_status.title"),
        tooltipSubtitle: translate("llm.prompts.column.change_status.tooltip"),
        sortOrder: sorting.dataIndex === "changeStatus" ? sorting.sortDirection : undefined,
        sorter: true,
        align: "left",
        width: 150,
        hidden: true,
        render: (changeStatus?: string | null) => {
            if (changeStatus === ChangeStatus.New) {
                return (
                    <IntentCell
                        label={translate("llm.prompts.column.change_status.new")}
                        bgColor={colorsPalettes.green[100]}
                        textColor={colorsPalettes.green[500]}
                    />
                );
            }
            if (changeStatus === ChangeStatus.Lost) {
                return (
                    <IntentCell
                        label={translate("llm.prompts.column.change_status.lost")}
                        bgColor={colorsPalettes.red[100]}
                        textColor={colorsPalettes.red[500]}
                    />
                );
            }
            return NO_DATA_SIGN;
        },
    },
    {
        key: "isBrandMentioned",
        dataIndex: "isBrandMentioned",
        title: translate("llm.url_analysis.table.column.brand_mentioned.title"),
        tooltipSubtitle: translate("llm.url_analysis.table.column.brand_mentioned.tooltip"),
        sortOrder: undefined,
        align: "left",
        width: 160,
        render: (isBrandMentioned) =>
            isBrandMentioned ? (
                <CheckCircle
                    size="xs"
                    color={colorsPalettes.carbon[400]}
                />
            ) : null,
    },
    ...(hasCompetitors
        ? ([
              {
                  key: "competitorsMentioned",
                  dataIndex: "competitorsMentioned",
                  title: translate("llm.url_analysis.table.column.competitors_mentioned.title"),
                  tooltipSubtitle: translate("llm.url_analysis.table.column.competitors_mentioned.tooltip"),
                  sortOrder: undefined,
                  align: "left",
                  width: 200,
                  hidden: false,
                  render: (competitors: string[] | null | undefined) =>
                      competitors?.length ? (
                          <ChipOverflowCell labels={competitors.map((competitor) => ({ text: competitor }))} />
                      ) : (
                          NO_DATA_SIGN
                      ),
              },
          ] satisfies Array<ColumnType<IAnalyzedURL>>)
        : []),
    {
        key: "sourceCategory",
        dataIndex: "sourceCategory",
        title: translate("llm.sources_report.url_analysis.table.column.source_category.title"),
        tooltipSubtitle: translate("llm.sources_report.url_analysis.table.column.source_category.tooltip"),
        sortOrder: sorting.dataIndex === "sourceCategory" ? sorting.sortDirection : undefined,
        sorter: true,
        align: "left",
        width: 175,
        render: (sourceCategory) => <SourceBadge sources={[{ name: sourceCategory as TopicSourceType }]} />,
    },
    {
        key: "topics",
        dataIndex: "topics",
        title: translate("llm.sources_report.url_analysis.table.column.topics.title"),
        tooltipSubtitle: translate("llm.sources_report.url_analysis.table.column.topics.tooltip"),
        sortOrder: sorting.dataIndex === "topics" ? sorting.sortDirection : undefined,
        sorter: true,
        align: "left",
        width: 150,
        render: (topics: string[]) => <ChipOverflowCell labels={topics.map((topic) => ({ text: topic }))} />,
    },
    {
        key: "responseCount",
        dataIndex: "responseCount",
        title: translate("llm.sources_report.url_analysis.table.column.responses.title"),
        tooltipSubtitle: translate("llm.sources_report.url_analysis.table.column.responses.tooltip"),
        sortOrder: sorting.dataIndex === "responseCount" ? sorting.sortDirection : undefined,
        sorter: true,
        align: "left",
        width: 150,
        render: (value: number) => value,
    },
];
